import { useEffect, useRef, useState } from 'react';
import './my-orgs.styles.scss';
import { useSelector } from 'react-redux';
import AgeCheck from '@/components/layout-auth/age-check/age-check.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEarthAmericas } from '@fortawesome/free-solid-svg-icons';
import { Route, Routes, useNavigate } from 'react-router-dom';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import org_placeholder from '@/assets/placeholder-icon-org.jpg';
import MyOrgView from './my-org-view.component';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { getOrgInvitesFromDb } from '@/utils/getDataFromDb';
import OrgInviteHandlerModal from '@/components/HOME/invite-handler/org-invite-handler-modal.component';

const MyOrgs = ({ orgs }) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const [orgsToDisplay, setOrgsToDisplay] = useState([]);
  const [loading, setLoading] = useState(true);
  // pending invites keyed by orgId
  const [pendingInvites, setPendingInvites] = useState({});
  const [invitesLoaded, setInvitesLoaded] = useState(false);
  const [reviewInvite, setReviewInvite] = useState(null);
  const invitesRef = useRef({});
  const navigate = useNavigate('/quote');

  // inviteUserToOrg adds invitees to orgRegs before they accept, so their
  // orgs list already includes the org. track pending invites so the org
  // stays locked until the invite is accepted.
  useEffect(() => {
    if (!userProfile?.email) return;
    let unsubscribe;
    invitesRef.current = {};
    getOrgInvitesFromDb({
      'email': userProfile.email,
      'callback': (data, id) => {
        invitesRef.current = { ...invitesRef.current, [id]: data };
      },
      'onLoaded': () => {
        const pending = {};
        for (const invite of Object.values(invitesRef.current)) {
          if (invite.status === 'pending') pending[invite.orgId] = invite;
        }
        setPendingInvites(pending);
        setInvitesLoaded(true);
      },
      'handleListener': (unsub) => { unsubscribe = unsub; }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    }
  }, [userProfile?.email])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 3000)
    changeDocumentTitle('Gamably | Your Organizations');
    return () => {
      if (timeout) clearTimeout(timeout);
    }
  }, [])

  useEffect(() => {
    console.log(orgs);
    // null means no orgs (or not loaded yet). clearing the list covers a
    // user removed from their last org; loading is left alone so the first
    // load still shows the loader until orgs arrive or the timeout ends it.
    if (!orgs) {
      setOrgsToDisplay([]);
      return;
    }
    let sortedOrgs = Object.values(orgs)
      .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1);
    setOrgsToDisplay([...sortedOrgs])
    setLoading(false);
  }, [orgs])

  return (
    <div className='my-orgs'>
      <AgeCheck />
      {/* <Layout> */}
      <Routes>
        <Route path=''
          element={
            <div className={`orgs-content ${screenSize}`}>
              <div className={`g-card ${screenSize}`}>
                <div className='section-title with-icon'>
                  <span>Your Organizations</span>
                  {
                    (userProfile) &&
                    <button
                      className={`g-button ${screenSize === 'mobile' ? 'small-btn' : ''}`}
                      onClick={() => navigate('/quote')}>
                      Create{
                        (screenSize === 'largeTablet' || screenSize === 'desktop') &&
                        ' a New Organization'
                      }
                      <FontAwesomeIcon
                        icon={faEarthAmericas}
                        size="lg"
                        className='button-icon' />
                    </button>
                  }
                </div>

                <hr />
                {
                  (loading)
                    ?
                    <>
                      <div></div>
                      <div className='center'><ThreeDotsLoader /></div>
                    </>
                    :
                    (orgsToDisplay.length > 0)
                      ?
                      <div className='org-list'>
                        {
                          orgsToDisplay.map(o => {
                            const invite = pendingInvites[o.orgId];
                            return (
                              <button
                                key={o.orgId}
                                className='org-list-item g-button'
                                onClick={() => invite ? setReviewInvite(invite) : navigate(o.path)}>
                                <div className='icon'>
                                  <ImageContainer
                                    src={o.icon ?? org_placeholder}
                                    className='org-icon' />
                                </div>
                                <div className='title'>
                                  {o.name}
                                  {
                                    (invite) &&
                                    <div className='meta'>Invite pending — click to review</div>
                                  }
                                </div>
                              </button>
                            )
                          })
                        }
                      </div>
                      :
                      <div className='center-col'>
                        <h3>No Organizations Yet...</h3>
                      </div>
                }
              </div>
              {
                (screenSize === 'desktop') &&
                <div className="g-space-2"></div>
              }
            </div>
          }></Route>
        <Route path=':orgPath'
          element={
            <div className='orgs-content'>
              <MyOrgView
                orgs={orgs}
                pendingInvites={pendingInvites}
                invitesLoaded={invitesLoaded} />
              {
                (screenSize === 'desktop') &&
                <div className="g-space-2"></div>
              }
            </div>
          }></Route>
      </Routes>
      {/* the invites listener clears the pending state once the invite is
          accepted or declined, so removeApprovedInvite only closes the modal */}
      <OrgInviteHandlerModal
        invite={reviewInvite}
        cancel={() => setReviewInvite(null)}
        removeApprovedInvite={() => setReviewInvite(null)} />
    </div>
  )
}

export default MyOrgs